import { TitleCasePipe } from '@angular/common';
import { RequestTypesKeys } from '@app/enums/RequestTypes';
import { getTravelRequestEnumStringValue, TravelRequestEnum } from '@app/enums/travel-request.enum';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ProcessCellForExportParams } from '@medpacesoftwaredevelopment/designsystem/lib/components/ag-grid';
import { DateFormat } from '@models/date-format';
import { PaymentRequestStatus, RequestStatusNames, TravelRequestStatus } from '@models/request';
import { StudyExpense } from '@models/study';
import { StudyExpenseTrackingDataRow } from '@models/studyExpenseTrackingDataRow';
import { emptyValue } from '@utility/constants';

export function mapToExportValue(params: ProcessCellForExportParams<StudyExpenseTrackingDataRow, any>) {
    switch (params.column.getColId()) {
        case 'statusId':
        case 'expenseType':
            return params.formatValue(params.value);
        case 'visitName':
            return params.value?.name ?? emptyValue;
        case 'localCurrencyCode':
            return params.value?.isocode;
        case 'requestedAmount':
        case 'amountAuthorized':
        case 'totalUSD':
            return params.value ? params.value.toFixed(2) : '';
        case 'merchant':
            return params.value?.viewValue;
        case 'requestId':
            const requestType = params.node.data.requestType;
            if (requestType === RequestTypesKeys.REIMBURSEMENT) return `R-${params.value}`;
            else if (requestType === RequestTypesKeys.TRAVEL) return `T-${params.value}`;
            else if (requestType === RequestTypesKeys.STIPEND) return `S-${params.value}`;
        default:
            return params.value;
    }
}

export const colHeaders = {
    statusId: 'Request Status',
    statusChangedAt: 'Date of last status change',
    projectId: 'Project ID',
    requestId: 'Request ID',
    siteId: 'Site #',
    siteNumber: 'Site #',
    country: 'Country',
    patientId: 'Patient ID',
    patientNumber: 'Patient ID',
    visitName: 'Visit Name',
    visitStartDate: 'Visit Date',
    requestType: 'Request Type',
    receiptDate: 'Receipt Date',
    transactionDate: 'Transaction Date',
    merchant: 'Merchant',
    expenseType: 'Expense Type',
    localCurrencyCode: 'Local Currency',
    requestedAmount: 'Requested Amount (local)',
    amountAuthorized: 'Amount Authorized (local expense)',
    totalUSD: 'USD Expense',
    vendorId: 'Vendor ID',
    requestNotes: 'Expense Notes',
    erTrackingId: 'Expense Report Tracking ID',
    erReportingPeriod: 'ER Reporting Period',
    dateRequestAddedToER: 'Date Request Added to ER',
};

export function mapDataFormat(colId: string, row: StudyExpenseTrackingDataRow, model: any): string {
    const titlePipe = new TitleCasePipe();
    const datePipe = new dateFormatPipe();

    let val = '';

    switch (colId) {
        case 'statusId':
            val =
                row.requestType === RequestTypesKeys.TRAVEL
                    ? RequestStatusNames[TravelRequestStatus[model[colId]]]
                    : RequestStatusNames[PaymentRequestStatus[model[colId]]];
            break;
        case 'expenseType':
            val =
                row.requestType === RequestTypesKeys.TRAVEL
                    ? titlePipe.transform(getTravelRequestEnumStringValue(model[colId] as TravelRequestEnum))
                    : ((model[colId] as StudyExpense)?.expenseDefaultValue ?? '');
            break;

        case 'visitName':
            val = model[colId]?.name;
            break;
        case 'statusChangedAt':
        case 'receiptDate':
        case 'transactionDate':
        case 'dateRequestAddedToER':
            val = model[colId] ? datePipe.transform(model[colId], DateFormat.dateOnly, 'local') : '';
            break;
        case 'localCurrencyCode':
            val = model[colId]?.isocode;
            break;
        case 'requestedAmount':
        case 'amountAuthorized':
        case 'totalUSD':
            val = model[colId] ? model[colId].toFixed(2) : '';
            break;
        case 'merchant':
            val = model[colId]?.viewValue;
            break;
        case 'requestId':
            const requestType = row.requestType;
            if (requestType === RequestTypesKeys.REIMBURSEMENT) val = `R-${model[colId]}`;
            else if (requestType === RequestTypesKeys.TRAVEL) val = `T-${model[colId]}`;
            else if (requestType === RequestTypesKeys.STIPEND) val = `S-${model[colId]}`;
            break;
        default:
            val = model[colId];
            break;
    }

    return val;
}
